import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import AuthRightSection from '../components/AuthRightSection';
import { authApi } from '../services/api';

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  // Email validation regex
  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate email format
    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }

    if (!isValidEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);

    try {
      // Send password reset email using backend API
      await authApi.forgotPassword(email);

      // Always show success message for security (don't reveal if user exists)
      setSubmitted(true);
    } catch (err: any) {
      console.error('Password reset error:', err);

      // For security, show generic success message even on error
      // This prevents email enumeration attacks
      setSubmitted(true);

      // Only show specific errors for non-user-related issues
      if (err.response?.status === 429) {
        setSubmitted(false);
        setError('Too many requests. Please try again later');
      } else if (err.message === 'Network Error' || err.code === 'ERR_NETWORK') {
        setSubmitted(false);
        setError('Network error. Please check your connection and try again');
      } else if (err.response?.status === 0) {
        setSubmitted(false);
        setError('Backend server is not running. Please try again later');
      }
      // For other errors, we still show success message to prevent email enumeration
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="bg-[#ffffff] relative w-full min-h-screen flex flex-col lg:flex-row"
      data-name="Forgot Password"
    >
      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center p-4 sm:p-6 lg:p-8">
        <div className="flex flex-col gap-8 sm:gap-12 lg:gap-20 items-center justify-center max-w-[673px] w-full">
          <div className="flex flex-col gap-8 items-start justify-start w-full">
            {!submitted ? (
              <>
                {/* Header */}
                <div className="flex flex-col gap-4 items-start justify-center w-full">
                  <div className="font-['Ovo',_serif] text-[#000000] text-[24px] sm:text-[28px] lg:text-[32px] text-center uppercase w-full">
                    RESET YOUR PASSWORD
                  </div>
                  <div className="font-['Ovo',_serif] font-normal text-[#767472] text-[16px] text-center w-full">
                    Enter your email address and we'll send you a link to reset your password.
                  </div>
                </div>

                {/* Error message */}
                {error && (
                  <div
                    className="bg-red-50 border border-red-300 text-red-700 px-4 py-3 rounded w-full"
                    role="alert"
                  >
                    {error}
                  </div>
                )}

                {/* Form */}
                <form
                  onSubmit={handleSubmit}
                  className="flex flex-col gap-8 items-center justify-center w-full"
                >
                  <div className="flex flex-col gap-1 w-full">
                    <label
                      htmlFor="email"
                      className="font-['Ovo',_serif] font-normal text-[#1f1f1f] text-[16px]"
                    >
                      Email Address
                    </label>
                    <input
                      id="email"
                      type="email"
                      placeholder="Enter your registered email address"
                      className="h-14 w-full border border-[#eaeaea] px-6 py-4 font-['Ovo',_serif] text-[16px] text-[#1f1f1f] placeholder:text-[#767472] focus:outline-none focus:border-[#c6a664] focus:ring-1 focus:ring-[#c6a664]"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      disabled={loading}
                      autoFocus
                      aria-invalid={!!error}
                      aria-describedby={error ? 'email-error' : undefined}
                    />
                  </div>

                  <button
                    type="submit"
                    className="border border-[#1f1f1f] border-solid px-6 py-3 font-['Ovo',_serif] text-[#000000] text-[20px] uppercase bg-transparent hover:bg-[#1f1f1f] hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
                    disabled={loading}
                  >
                    {loading ? 'SENDING...' : 'SEND RESET LINK'}
                  </button>
                </form>

                {/* Back to Sign In */}
                <div className="flex flex-row gap-1 items-center justify-center w-full">
                  <div className="font-['Ovo',_serif] font-normal text-[#000000] text-[16px]">
                    Remember your password?
                  </div>
                  <Link
                    to="/signin"
                    className="font-['Ovo',_serif] font-semibold text-[#c6a664] text-[16px] underline"
                  >
                    Sign In
                  </Link>
                </div>
              </>
            ) : (
              <>
                {/* Success message */}
                <div className="flex flex-col gap-6 items-center justify-center w-full text-center">
                  {/* Success icon */}
                  <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center">
                    <svg
                      className="w-10 h-10 text-green-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>

                  <div className="font-['Ovo',_serif] text-[#000000] text-[24px] sm:text-[28px] lg:text-[32px] uppercase">
                    CHECK YOUR EMAIL
                  </div>

                  <div className="font-['Ovo',_serif] font-normal text-[#767472] text-[16px] max-w-md">
                    If an account exists for <strong className="text-[#1f1f1f]">{email}</strong>,
                    you will receive a password reset link shortly.
                  </div>

                  <div className="font-['Ovo',_serif] font-normal text-[#767472] text-[14px] max-w-md">
                    Please check your spam folder if you don't see the email in your inbox.
                  </div>

                  {/* Action buttons */}
                  <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto mt-4">
                    <Link
                      to="/signin"
                      className="border border-[#1f1f1f] border-solid px-6 py-3 font-['Ovo',_serif] text-[#000000] text-[18px] uppercase bg-transparent hover:bg-[#1f1f1f] hover:text-white transition-colors text-center"
                    >
                      BACK TO SIGN IN
                    </Link>
                    <button
                      onClick={() => {
                        setSubmitted(false);
                        setEmail('');
                        setError('');
                      }}
                      className="border border-[#c6a664] border-solid px-6 py-3 font-['Ovo',_serif] text-[#c6a664] text-[18px] uppercase bg-transparent hover:bg-[#c6a664] hover:text-white transition-colors"
                    >
                      SEND AGAIN
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Mobile-only benefits section */}
          <div className="block md:hidden w-full bg-[#f8f6f2] p-6 rounded-lg">
            <div className="text-center mb-4">
              <div className="font-['Ovo',_serif] text-[#000000] text-[20px] uppercase">
                Get ready to tout your CLOUT!
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-[#c6a664] rounded-full mt-2 flex-shrink-0"></div>
                <div className="font-['Ovo',_serif] text-[#000000] text-[14px]">
                  Discover your initial CLOUT score
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-[#c6a664] rounded-full mt-2 flex-shrink-0"></div>
                <div className="font-['Ovo',_serif] text-[#000000] text-[14px]">
                  Complete your profile to become CLOUT certified
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-[#c6a664] rounded-full mt-2 flex-shrink-0"></div>
                <div className="font-['Ovo',_serif] text-[#000000] text-[14px]">
                  Invite peers to endorse you and build your CLOUT!
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Illustration */}
      <div className="hidden md:block flex-1 min-h-[400px] lg:min-h-screen">
        <AuthRightSection />
      </div>
    </div>
  );
};

export default ForgotPassword;
